import React, { forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';

import TableContext from '../Table/TableContext';
import TableComponentsContext from '../Table/TableComponentsContext';
import { TableCellRoot } from './TableCellStyles';
import { conditionalClassNames } from '../../../../UtilityComponents/Util';

/**
 *
 * @param {Object} classes - {
 *  root - Classes applied to all TableCell
 *  head - Classes applied to cell of header row
 *  body - Classes applied to cell of body row
 *  footer - classes applied to cell of footer row
 *  sizeSmall - classes applied to cell when size == small
 *  paddingCheckbox - classes applied to cell when padding === 'checkbox'
 *  paddingNone - classes applied to cell when padding === 'none'
 * stickyHeader - classes applied when stickyHeader == true
 * }
 * @param {String} align
 * @param {String} padding
 * @param {String} size
 * @param {String} variant
 */

const TableCell = ({
	classes = {},
	align = 'inherit',
	padding,
	size = 'medium',
	variant: variantProp,
	forwardedRef,
	...other
}) => {
	const table = useContext(TableContext);
	const tablelvl2 = useContext(TableComponentsContext) || {};

	const { variant: tableVariant, theme } = tablelvl2;
	const isHeadCell = tableVariant === 'head';
	const isBodyCell = tableVariant === 'body';
	const isFooterCell = tableVariant === 'footer';

	const variant = variantProp || tableVariant;
	const stickyHeader = variant === 'head' && table && table.stickyHeader;

	const { root,
		head,
		body,
		footer,
		sizeSmall,
		paddingCheckbox,
		paddingNone,
		stickyHeader: stickyHeaderClass } = classes;

	const classNames = conditionalClassNames({
		[root]: root,
		[head]: head && isHeadCell,
		[body]: body && isBodyCell,
		[footer]: footer && isFooterCell,
		[sizeSmall]: sizeSmall && size === 'small',
		[paddingCheckbox]: paddingCheckbox && padding === 'checkbox',
		[paddingNone]: paddingNone && padding === 'none',
		[stickyHeaderClass]: stickyHeaderClass && stickyHeader
	});

	return (
		<TableCellRoot
			className={classNames}
			ref={forwardedRef}
			variant={variant}
			size={size}
			stickyHeader={stickyHeader}
			padding={padding}
			align={align}
			theme={theme}
			{...other}
		/>
	);
};

TableCell.propTypes = {
	align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
	classes: PropTypes.object,
	padding: PropTypes.string,
	size: PropTypes.oneOf(['medium', 'small']),
	variant: PropTypes.oneOf(['body', 'footer', 'head', 'child'])
};

export default forwardRef((props, ref) => <TableCell {...props} forwardedRef={ref} />);
